import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { app, functions } from '../firebase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { normalizeSchedule, createDefaultSchedule, DAYS_OF_WEEK } from '../utils/schedule';

const db = getFirestore(app);

export default function RoomDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const ref = doc(db, 'rooms', id);
    getDoc(ref).then(snap => {
      if (!snap.exists()) {
        alert('Room not found');
        navigate('/rooms');
        return;
      }
      const data = snap.data();
      setRoom({ id: snap.id, ...data, schedule: normalizeSchedule(data.schedule) });
    }).catch(e => console.error('fetch room', e));

    // fetch participants linked to this room
    const q = query(collection(db, 'participants'), where('linkedRoomIds', 'array-contains', id));
    getDocs(q).then(snap => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setParticipants(list);
    }).catch(e => console.error('fetch participants for room', e)).finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <Box sx={{ p: 2 }}>Loading room...</Box>;
  if (!room) return null;

  const schedule = room.schedule || createDefaultSchedule();

  const handleSyncRoom = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const callable = httpsCallable(functions, 'syncRoomDevices');
      const response = await callable({ roomId: id });
      const data = response && response.data ? response.data : {};
      const targeted = typeof data.targetedDevices === 'number' ? data.targetedDevices : 0;
      const successes = typeof data.successes === 'number' ? data.successes : 0;
      const failures = typeof data.failures === 'number' ? data.failures : 0;

      if (targeted === 0) {
        alert('No bonded devices linked to this room received the sync command.');
        return;
      }

      if (failures > 0) {
        alert(`Sync requested for ${targeted} device(s). ${successes} succeeded, ${failures} failed. Check function logs for details.`);
        return;
      }

      alert(`Sync command dispatched to ${successes} device(s).`);
    } catch (error) {
      console.error('syncRoomDevices callable failed', error);
      if (error && error.code === 'permission-denied') {
        alert('You are not authorized to sync room parameters. Please reauthenticate or contact an administrator.');
      } else if (error && error.code === 'unauthenticated') {
        alert('Authentication required. Please sign in again and retry.');
      } else {
        const message = (error && error.message) || 'Unknown error while syncing room.';
        alert(`Failed to sync room: ${message}`);
      }
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5">Room: {room.name}</Typography>
      <Box sx={{ mt: 2 }}>
        <Button component={Link} to={`/rooms/${id}/edit`} variant="outlined">Edit</Button>
        <Button onClick={() => navigate('/rooms')} sx={{ ml: 1 }} variant="contained">Back to Rooms</Button>
        <Button
          onClick={handleSyncRoom}
          sx={{ ml: 1 }}
          variant="contained"
          color="secondary"
          disabled={syncing}
        >
          {syncing ? 'Syncing...' : 'Sync room'}
        </Button>
      </Box>
      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="h6">Listening schedule</Typography>
        {schedule.alwaysOn ? (
          <Typography sx={{ mt: 1 }}>Devices assigned to this room stay active at all times.</Typography>
        ) : (
          <Box component="ul" sx={{ pl: 2, mt: 1 }}>
            {DAYS_OF_WEEK.map(({ key, label }) => {
              const day = schedule.days[key];
              return (
                <li key={key}>
                  <Typography>
                    <strong>{label}:</strong> {day.enabled ? `${day.start} - ${day.end}` : 'Inactive'}
                  </Typography>
                </li>
              );
            })}
          </Box>
        )}
      </Paper>
      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="h6">Participants in this room</Typography>
        {participants.length === 0 ? (
          <Typography>No participants linked to this room.</Typography>
        ) : (
          <ul>
            {participants.map(p => (
              <li key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: p.status === 'online' ? 'success.main' : 'error.main',
                  }}
                />
                <Typography component="span">{p.name} ({p.status || 'unknown'})</Typography>
              </li>
            ))}
          </ul>
        )}
      </Paper>
    </Box>
  );
}
